import React, { useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
} from 'chart.js';
import styled from 'styled-components';
import { IChartBaseData } from 'features/report/services/types/reportStaff';

interface AppointmentChartProps {
  data?: IChartBaseData[];
  bookOnline?: number;
  localAppointments?: number;
  isYearComparasion?: boolean;
  year?: number;
}

const AppointmentChart: React.FC<AppointmentChartProps> = ({
  data = [],
  bookOnline,
  localAppointments,
  isYearComparasion = false,
  year = 0,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const defaultColor = '#3AADD9';
  const hoverColor = '#34c5ff';
  const comparisonColor = '#4A63E7';

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: isYearComparasion,
          external: isYearComparasion
            ? undefined
            : function (context: any) {
                let tooltipEl = document.getElementById('chartjs-tooltip');

                if (!tooltipEl) {
                  tooltipEl = document.createElement('div');
                  tooltipEl.id = 'chartjs-tooltip';
                  tooltipEl.innerHTML = '<div></div>';
                  document.body.appendChild(tooltipEl);
                }

                const tooltipModel = context.tooltip;
                if (tooltipModel.opacity === 0) {
                  tooltipEl.style.opacity = '0';
                  return;
                }

                tooltipEl.style.opacity = '1';
                tooltipEl.style.position = 'absolute';
                tooltipEl.style.padding = '8px 12px';
                tooltipEl.style.background = 'white';
                tooltipEl.style.borderRadius = '16px';
                tooltipEl.style.boxShadow = '0px 2px 10px rgba(0, 0, 0, 0.1)';
                tooltipEl.style.pointerEvents = 'none';
                tooltipEl.style.transition = 'opacity 0.2s';

                const value = tooltipModel.dataPoints[0].formattedValue;

                tooltipEl.innerHTML = `<div style="text-align: center;">${value}</div>`;

                const position = context.chart.canvas.getBoundingClientRect();
                const barCenterX = position.left + tooltipModel.caretX;
                const tooltipWidth = tooltipEl.offsetWidth;

                tooltipEl.style.left = `${barCenterX - tooltipWidth / 2}px`;
                tooltipEl.style.top = `${
                  position.top + tooltipModel.caretY - 50
                }px`;
              },
          titleFont: {
            size: 18,
            family: 'Poppins',
            weight: 'bold',
          },
          bodyFont: {
            size: 16,
            family: 'Poppins',
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          border: {
            display: false,
          },
          ticks: {
            font: {
              size: 16,
            },
            autoSkip: true,
            maxRotation: 45,
            minRotation: 0,
          },
        },
        y: {
          grid: {
            drawBorder: false,
            color: '#f1f1f1',
          },
          border: {
            display: false,
          },
          ticks: {
            font: {
              size: 14,
            },
            stepSize: 250,
            max: 500,
          },
        },
      },
      onHover: (event: any, elements: any) => {
        if (!isYearComparasion) {
          if (elements && elements.length > 0) {
            setHoveredIndex(elements[0].index);
          } else {
            setHoveredIndex(null);
          }
        }
      },
    }),
    [isYearComparasion]
  );

  const chartData: ChartData<
    'bar',
    (number | [number, number] | null)[],
    unknown
  > | null = useMemo(() => {
    if (isYearComparasion) {
      return {
        labels: data?.map((i) => i.key),
        datasets: [
          {
            label: (year - 1).toString(),
            data: data?.map((i) => i.valueOld || 0),
            backgroundColor: defaultColor,
            borderWidth: 0,
            borderRadius: 3,
            barPercentage: 0.9,
            categoryPercentage: 0.8,
          },
          {
            label: year.toString(),
            data: data?.map((i) => i.value),
            backgroundColor: comparisonColor,
            borderWidth: 0,
            borderRadius: 3,
            barPercentage: 0.9,
            categoryPercentage: 0.8,
          },
        ],
      };
    } else {
      return {
        labels: data?.map((i) => i.key),
        datasets: [
          {
            label: '',
            data: data?.map((i) => i.value),
            backgroundColor: (context: any) => {
              const index = context.dataIndex;
              return index === hoveredIndex ? hoverColor : defaultColor;
            },
            hoverBackgroundColor: hoverColor,
            borderWidth: (context: any) => {
              const index = context.dataIndex;
              return index === hoveredIndex ? 1 : 0;
            },
            borderColor: hoverColor,
            borderRadius: 3,
            barPercentage: 1,
          },
        ],
      };
    }
  }, [
    data,
    hoveredIndex,
    isYearComparasion,
    year,
    defaultColor,
    hoverColor,
    comparisonColor,
  ]);

  return (
    <ChartContainer>
      <ChartHeader>
        <ChartTitle>Appointment</ChartTitle>
        {isYearComparasion && (
          <LegendContainer>
            <LegendItem>
              <ColorBox color={defaultColor} />
              <YearLabel>{year - 1}</YearLabel>
            </LegendItem>
            <LegendItem>
              <ColorBox color={comparisonColor} />
              <YearLabel>{year}</YearLabel>
            </LegendItem>
          </LegendContainer>
        )}
        <StatsContainer>
          <StatItem>
            <StatLabel>Book Online : </StatLabel>
            <StatValue>{bookOnline || '0'}</StatValue>
          </StatItem>
          <StatItem>
            <StatLabel>Local : </StatLabel>
            <StatValue>{localAppointments || '0'}</StatValue>
          </StatItem>
        </StatsContainer>
      </ChartHeader>

      <ChartWrapper>
        <Bar
          data={chartData}
          // @ts-ignore
          options={options}
          plugins={[
            CategoryScale,
            LinearScale,
            BarElement,
            Title,
            Tooltip,
            Legend,
          ]}
        />
      </ChartWrapper>
    </ChartContainer>
  );
};

export default AppointmentChart;

const ChartContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const ChartTitle = styled.div`
  font-size: 26px;
  font-weight: 400;
  color: #1e293b;
  text-transform: capitalize;
`;

const StatsContainer = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StatValue = styled.span`
  color: #c84b31;
  font-family: Poppins;
  font-weight: 600;
  font-size: 26px;
  line-height: 100%;
  letter-spacing: 0%;
`;

const StatLabel = styled.span`
  font-size: 24px;
  color: #1e293b;
  margin-right: 5px;
  font-family: Poppins;
  font-weight: 400;
  line-height: 100%;
  letter-spacing: 0%;
  text-transform: capitalize;
`;

const ChartWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: relative;
  min-height: 200px;
`;

const LegendContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  font-family: 'Poppins', sans-serif;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ColorBox = styled.div`
  width: 40px;
  height: 20px;
  background-color: ${(props) => props.color};
  margin-bottom: 5px;
`;

const YearLabel = styled.span`
  color: #272727;
  font-size: 26px;
  font-weight: 300;
`;
